// Country detail — tabbed: Locale & defaults · Ledger codes (editable) · Pricing.
// Chart of accounts is editable: add/edit accounts (flat — no sub-accounts by default).
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { focusModal } from '../shared/modal.js';
import { DATE_FORMATS } from '../shared/formats.js';
import { rowDelete } from '../shared/confirm.js';
import { flashId, flashRow, revealRow } from '../shared/listnav.js';

const TABS = [
  { key: 'locale',   label: 'Locale & defaults' },
  { key: 'ledger',   label: 'Ledger codes' },
  { key: 'entities', label: 'Entity types' },
  { key: 'tax',      label: 'VAT codes' },
  { key: 'pricing',  label: 'Pricing' },
  { key: 'email',    label: 'Email Templates' },
];
// tax.code_kind — value → friendly label
const TAX_KINDS = [
  { v: 'output',      l: 'Output (on sales)' },
  { v: 'input',       l: 'Input (on purchases)' },
  { v: 'zero_rated',  l: 'Zero-rated' },
  { v: 'exempt',      l: 'Exempt' },
  { v: 'no_tax',      l: 'No tax' },
  { v: 'withholding', l: 'Withholding' },
  { v: 'other',       l: 'Other' },
];
const TAX_KIND_LABEL = v => (TAX_KINDS.find(k => k.v === v) || {}).l || v;
// sample values used only for the live preview in the editor
const SAMPLE_VARS = {
  reset_link: 'https://app-staging.aboo.co.za/reset-password/EXAMPLE-TOKEN',
  login_link: 'https://app-staging.aboo.co.za/login',
  email: 'joe.soap@company.co.za',
  name: 'Joe Soap',
};
const CONTROL_TYPES = ['debtors', 'creditors', 'vat', 'stock', 'bank'];

export function countryDetailScreen(id){
  const country   = signal(null);
  const err       = signal('');
  const activeTab = signal('locale');
  const accounts  = signal({ data: [], total: 0, total_pages: 1, loaded: false });
  const acctPage  = signal(1);
  const acctPer   = signal(DEFAULT_PER_PAGE);
  const tiers     = signal({ data: [], total: 0, loaded: false });
  const cats      = signal([]);                 // ledger categories for the form select
  const gaapCats  = signal([]);                 // this country's own GAAP list (migration 180) — empty for most
  const catRoots  = signal([]);                 // the 5 shared roots — group the GAAP select

  // ---- account form state ----
  const formOpen = signal(false);
  const formId   = signal(null);                // null = create
  const fCode = signal(''), fName = signal(''), fCategory = signal('');
  const fGaap = signal('');                       // the code's GAAP category id ('' = none)
  const fControl = signal(''), fBank = signal(false), fActive = signal(true);
  const fDesc = signal(''), fSort = signal('');
  const fTax = signal('');                        // per-account default VAT/tax code id ('' = none)
  const formErr = signal(''), saving = signal(false);

  // ---- country edit form state ----
  const langs = signal([]), currs = signal([]);
  const cOpen = signal(false);
  const eIso2 = signal(''), eIso3 = signal(''), eName = signal(''), eLang = signal(''), eCurr = signal('');
  const eDate = signal('yyyy-MM-dd'), eTaxLabel = signal('VAT');
  const eStd = signal('');                        // accounting standard: IFRS / GAAP
  const cErr = signal(''), cSaving = signal(false);

  // ---- entity type state ----
  const entityTypes = signal({ data: [], total: 0, loaded: false });
  const etOpen = signal(false), etId = signal(null);
  const etCode = signal(''), etName = signal(''), etSort = signal(''), etActive = signal(true);
  const etErr = signal(''), etSaving = signal(false);
  // default-codes picker (subset of the country chart)
  const codesOpen = signal(false), codesEtId = signal(null), codesEtName = signal('');
  const allAccts = signal([]);          // full country chart, loaded once for the picker
  const codeSel = signal([]);           // selected account ids
  const codeQ = signal('');
  // per-account name-override signals — each editable input binds its OWN signal (bound as the
  // object), so typing never re-runs the surrounding picker slot and the field keeps focus.
  const descSig = {};
  const dsig = (aid) => (descSig[aid] || (descSig[aid] = signal('')));
  const acctName = (aid) => { const a = allAccts.value.find(x => x.id === aid); return (a && a.name) || ''; };
  const codesErr = signal(''), codesSaving = signal(false), codesLoading = signal(false);

  // ---- pricing tier form state ----
  const tOpen = signal(false), tId = signal(null);
  const tCode = signal(''), tName = signal(''), tMonth = signal(''), tYear = signal('');
  const tTxn = signal(''), tTurnover = signal(''), tUsers = signal(''), tSort = signal('');
  const tActive = signal(true), tTerminal = signal(false);
  const tErr = signal(''), tSaving = signal(false);

  // ---- tax / VAT codes state ----
  const taxCodes = signal({ regime: null, data: [], loaded: false });
  const txOpen = signal(false), txId = signal(null);
  const txCode = signal(''), txName = signal(''), txRate = signal(''), txKind = signal('output');
  const txCapital = signal(false);
  // Per-field statutory-value signals (built on open). Inputs bind these OBJECTS, so typing writes
  // only the field's own signal — never a signal the surrounding reactive slot reads — so the slot
  // never re-runs mid-type and the input keeps focus (mirrors the remittance-fields pattern).
  const txStatSig = {};
  const statSig = (key) => (txStatSig[key] || (txStatSig[key] = signal('')));
  const txActive = signal(true), txSort = signal('');
  const txErr = signal(''), txSaving = signal(false);
  // regime "reporting fields" editor (statutory field defs, e.g. SARS code / VAT SARS code)
  const rgOpen = signal(false), rgName = signal(''), rgFields = signal([]), rgNonce = signal(0);
  const rgErr = signal(''), rgSaving = signal(false);
  const statFields = () => (taxCodes.value.regime && taxCodes.value.regime.statutory_fields) || [];

  // ---- email templates state ----
  const emailTpls = signal({ data: [], loaded: false });
  const teOpen = signal(false);              // editor modal open
  const teKey = signal(''), teLabel = signal(''), teVars = signal([]);
  const teSubject = signal(''), teBody = signal(''), teCustom = signal(false);
  const teDefaultSubject = signal(''), teDefaultBody = signal('');
  const teErr = signal(''), teSaving = signal(false), tePreview = signal(true);

  // ---- shared inline delete (ledger codes / entity types / tiers) — keyed "kind:id" ----
  const delId = signal(null), deleting = signal(false), delErr = signal('');
  function armDel(key){ delErr.value = ''; delId.value = key; }
  function cancelDel(){ delId.value = null; delErr.value = ''; }
  async function doDel(url, reload){
    deleting.value = true; delErr.value = '';
    try { await api.del(url); delId.value = null; await reload(); }
    catch(e){ delErr.value = (e.data && e.data.error) || e.message || 'Delete failed'; }
    deleting.value = false;
  }
  const delCtl = (key, url, reload) => rowDelete({
    armed: delId.value === key, busy: deleting.value, error: delId.value === key ? delErr.value : '',
    onArm: () => armDel(key), onCancel: cancelDel, onDelete: () => doDel(url, reload) });

  (async () => {
    try { country.value = await api.get('/admin/countries/' + id); }
    catch (e) { err.value = e.status === 404 ? 'Country not found' : 'Failed to load country'; }
  })();
  (async () => {
    try { const r = await api.get('/admin/ledger-categories?per_page=200'); cats.value = (r.data || []).filter(c => c.parent_id);
          catRoots.value = (r.data || []).filter(c => !c.parent_id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)); }
    catch { cats.value = []; }
  })();
  (async () => {
    try { const r = await api.get('/admin/ledger-categories?standard=GAAP&country_id=' + encodeURIComponent(id) + '&per_page=200');
          gaapCats.value = (r.data || []).filter(c => c.parent_id); }
    catch { gaapCats.value = []; }
  })();
  // The root a category hangs under (its parent: categories sit one level under the 5 roots).
  const rootOfCat = cid => { const c = cats.value.find(x => String(x.id) === String(cid)); return c ? c.parent_id : null; };
  const rootOfGaap = gid => { const g = gaapCats.value.find(x => String(x.id) === String(gid)); return g ? g.parent_id : null; };
  // The group of the picked category, when this country's GAAP list has nothing under it yet: the GAAP select then
  // has nothing to offer the code, so the form says where to add one.
  const gaapGroupMissing = () => {
    if(!gaapCats.value.length || !fCategory.value) return '';
    const rid = rootOfCat(fCategory.value);
    const root = catRoots.value.find(r => r.id === rid);
    return root && !gaapCats.value.some(g => g.parent_id === rid) ? root.name : '';
  };
  (async () => { try { langs.value = (await api.get('/admin/languages?per_page=200')).data || []; } catch {} })();
  (async () => { try { currs.value = (await api.get('/admin/currencies?per_page=200')).data || []; } catch {} })();

  let acctQ = '', acctTimer;
  async function loadAccounts(force, locateId){
    if(accounts.value.loaded && !force) return;
    const params = new URLSearchParams({ per_page: acctPer.value, page: String(acctPage.value) });
    if(acctQ.trim()) params.set('q', acctQ.trim());
    if(locateId) params.set('locate', String(locateId));
    try { const r = await api.get('/admin/countries/' + id + '/accounts?' + params.toString());
          accounts.value = { data: r.data || [], total: r.total || 0, total_pages: r.total_pages || 1, loaded: true };
          if(locateId && r.locate_page) acctPage.value = r.locate_page; }
    catch { accounts.value = { data: [], total: 0, total_pages: 1, loaded: true }; }
  }
  function onAcctSearch(e){ acctQ = e.target.value; acctPage.value = 1; clearTimeout(acctTimer); acctTimer = setTimeout(() => loadAccounts(true), 220); }
  function onAcctPer(e){ acctPer.value = e.target.value; acctPage.value = 1; loadAccounts(true); }
  function acctGoPage(p){ acctPage.value = p; loadAccounts(true); }
  async function loadTiers(force){
    if(tiers.value.loaded && !force) return;
    try { const r = await api.get('/admin/countries/' + id + '/tiers?per_page=200');   // per-country pricing
          tiers.value = { data: r.data || [], total: r.total || 0, loaded: true }; }
    catch { tiers.value = { data: [], total: 0, loaded: true }; }
  }
  async function loadEntityTypes(force){
    if(entityTypes.value.loaded && !force) return;
    try { const r = await api.get('/admin/countries/' + id + '/entity-types?per_page=200');
          entityTypes.value = { data: r.data || [], total: r.total || 0, loaded: true }; }
    catch { entityTypes.value = { data: [], total: 0, loaded: true }; }
  }
  async function loadEmailTemplates(force){
    if(emailTpls.value.loaded && !force) return;
    try { const r = await api.get('/admin/countries/' + id + '/email-templates');
          emailTpls.value = { data: r.data || [], loaded: true }; }
    catch { emailTpls.value = { data: [], loaded: true }; }
  }
  const vatLabel = () => (country.value && country.value.tax_label) || 'VAT';   // per-country: VAT / GST / …
  async function loadTaxCodes(force){
    if(taxCodes.value.loaded && !force) return;
    try { const r = await api.get('/admin/countries/' + id + '/tax-codes');
          taxCodes.value = { regime: r.regime || null, data: r.data || [], loaded: true }; }
    catch { taxCodes.value = { regime: null, data: [], loaded: true }; }
  }
  function setTab(k){
    activeTab.value = k;
    if(k === 'ledger'){ loadAccounts(); loadTaxCodes(); }   // tax codes drive the per-account VAT badge + picker
    if(k === 'entities') loadEntityTypes();
    if(k === 'tax') loadTaxCodes();
    if(k === 'pricing') loadTiers();
    if(k === 'email') loadEmailTemplates();
  }

  // ---- tax / VAT code form ----
  function openTaxCreate(){
    txId.value = null; txCode.value = ''; txName.value = ''; txRate.value = ''; txKind.value = 'output';
    txCapital.value = false; statFields().forEach(f => statSig(f.key).value = ''); txActive.value = true; txSort.value = '';
    txErr.value = ''; txOpen.value = true; focusModal();
  }
  function openTaxEdit(t){
    txId.value = t.id; txCode.value = t.code || ''; txName.value = t.name || '';
    txRate.value = t.rate ?? ''; txKind.value = t.kind || 'output'; txCapital.value = !!t.is_capital;
    const st = t.statutory || {}; statFields().forEach(f => statSig(f.key).value = st[f.key] || '');
    txActive.value = t.is_active !== false; txSort.value = t.sort_order ?? '';
    txErr.value = ''; txOpen.value = true; focusModal();
  }
  function closeTax(){ txOpen.value = false; }
  async function saveTax(e){
    e.preventDefault();
    if(!txCode.value.trim() || !txName.value.trim()){ txErr.value = 'Code and name are required.'; return; }
    txSaving.value = true; txErr.value = '';
    const statutory = {}; statFields().forEach(f => { const v = statSig(f.key).value; if(v !== '' && v != null) statutory[f.key] = v; });
    const body = { code: txCode.value.trim(), name: txName.value.trim(),
                   rate: txRate.value === '' ? 0 : Number(txRate.value), kind: txKind.value,
                   is_capital: txCapital.value, statutory,
                   is_active: txActive.value, sort_order: txSort.value === '' ? 0 : Number(txSort.value) };
    try {
      if(txId.value) await api.put('/admin/tax-codes/' + txId.value, body);
      else await api.post('/admin/countries/' + id + '/tax-codes', body);
      txOpen.value = false; await loadTaxCodes(true);
    } catch(err){ txErr.value = (err.data && err.data.error) || err.message || 'Save failed'; }
    txSaving.value = false;
  }

  // ---- regime "reporting fields" editor ----
  function openRegime(){
    const r = taxCodes.value.regime || {};
    // Each row's label is its OWN signal — the reactive slot re-runs only on add/remove (array
    // identity changes), never on typing (that writes the row's label signal), so inputs keep focus.
    rgName.value = r.name || ''; rgFields.value = (r.statutory_fields || []).map(f => ({ key: f.key || '', label: signal(f.label || '') }));
    rgErr.value = ''; rgNonce.value++; rgOpen.value = true; focusModal();
  }
  function closeRegime(){ rgOpen.value = false; }
  function addField(){ rgFields.value = [...rgFields.value, { key: '', label: signal('') }]; rgNonce.value++; }
  function removeField(i){ rgFields.value = rgFields.value.filter((_, j) => j !== i); rgNonce.value++; }
  async function saveRegime(){
    rgSaving.value = true; rgErr.value = '';
    const fields = rgFields.value.filter(f => (f.label.value || '').trim()).map(f => ({ label: f.label.value.trim(), key: f.key || '' }));
    try {
      await api.put('/admin/countries/' + id + '/tax-regime', { name: rgName.value.trim(), statutory_fields: fields });
      rgOpen.value = false; await loadTaxCodes(true);
    } catch(err){ rgErr.value = (err.data && err.data.error) || err.message || 'Save failed'; }
    rgSaving.value = false;
  }

  // ---- email template editor ----
  function openTemplate(t){
    teKey.value = t.key; teLabel.value = t.label; teVars.value = t.variables || [];
    teSubject.value = t.subject; teBody.value = t.body_html; teCustom.value = t.is_custom;
    teDefaultSubject.value = t.default_subject; teDefaultBody.value = t.default_body_html;
    teErr.value = ''; tePreview.value = true; teOpen.value = true; focusModal();
    // the iframe was loaded while the modal was display:none (blank) — re-set srcdoc once it's
    // visible so the preview shows on open, not only after the first keystroke.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = document.querySelector('.tpl-preview');
      if(el) el.srcdoc = previewHtml();
    }));
  }
  function closeTemplate(){ teOpen.value = false; }
  function insertVar(v){
    const token = '{{' + v + '}}';
    const el = document.getElementById('te-body');
    if(el && typeof el.selectionStart === 'number'){
      const s = el.selectionStart, e = el.selectionEnd, val = teBody.value;
      teBody.value = val.slice(0, s) + token + val.slice(e);
      requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + token.length; });
    } else { teBody.value = teBody.value + token; }
  }
  function useDefault(){ teSubject.value = teDefaultSubject.value; teBody.value = teDefaultBody.value; }
  async function saveTemplate(){
    if(!teSubject.value.trim() || !teBody.value.trim()){ teErr.value = 'Subject and body are required.'; return; }
    teSaving.value = true; teErr.value = '';
    try {
      await api.put('/admin/countries/' + id + '/email-templates/' + teKey.value,
                    { subject: teSubject.value.trim(), body_html: teBody.value, is_active: true });
      teOpen.value = false; await loadEmailTemplates(true);
    } catch(e){ teErr.value = (e.data && e.data.error) || e.message || 'Save failed'; }
    teSaving.value = false;
  }
  async function resetTemplate(){
    teSaving.value = true; teErr.value = '';
    try {
      await api.del('/admin/countries/' + id + '/email-templates/' + teKey.value);
      teOpen.value = false; await loadEmailTemplates(true);
    } catch(e){ teErr.value = (e.data && e.data.error) || e.message || 'Reset failed'; }
    teSaving.value = false;
  }
  // brand-shell wrap for the live preview (mirrors the server; sample values substituted)
  function previewHtml(){
    let body = teBody.value;
    for(const [k, v] of Object.entries(SAMPLE_VARS)) body = body.split('{{' + k + '}}').join(v);
    return '<!doctype html><html><body style="margin:0;background:#eef2f3;padding:16px">' +
      '<div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;max-width:520px;margin:0 auto;color:#1f2937">' +
      '<div style="padding:16px 4px"><span style="font-weight:800;font-size:20px;color:#0E2E33">aboo</span></div>' +
      '<div style="border:1px solid #e5e7eb;border-radius:16px;padding:24px 22px;background:#fff">' + body + '</div>' +
      '<p style="color:#9ca3af;font-size:12px;text-align:center;margin:16px 0">© aboo</p></div></body></html>';
  }

  // ---- form open/close/save ----
  function openCreate(){
    formId.value = null; fCode.value = ''; fName.value = ''; fCategory.value = ''; fGaap.value = '';
    fControl.value = ''; fBank.value = false; fActive.value = true; fDesc.value = ''; fSort.value = '';
    fTax.value = '';
    formErr.value = ''; formOpen.value = true; focusModal();
    loadTaxCodes();
  }
  function openEdit(a){
    formId.value = a.id; fCode.value = a.code || ''; fName.value = a.name || '';
    fCategory.value = a.category_id || ''; fControl.value = a.control_type || '';
    fGaap.value = a.gaap_category_id || '';
    fBank.value = !!a.is_bank; fActive.value = a.is_active !== false;
    fDesc.value = a.description || ''; fSort.value = a.sort_order ?? '';
    fTax.value = a.default_tax_code_id || '';
    formErr.value = ''; formOpen.value = true; focusModal();
    loadTaxCodes();
  }
  function closeForm(){ formOpen.value = false; }
  // A new IFRS category under another root leaves the GAAP category behind: the code's GAAP category sits
  // under the same root (Assets with Assets…), so a choice under the old root is cleared.
  function onCategory(e){
    fCategory.value = e.target.value;
    if(gaapCats.value.length && fGaap.value && rootOfGaap(fGaap.value) !== rootOfCat(fCategory.value)) fGaap.value = '';
  }

  async function save(e){
    e.preventDefault();
    if(!fCode.value.trim() || !fName.value.trim() || !fCategory.value){
      formErr.value = 'Code, name and category are required.'; return;
    }
    if(!/^[A-Za-z0-9]{4}$/.test(fCode.value.trim())){
      formErr.value = 'Code must be exactly 4 letters or digits.'; return;
    }
    if(gaapCats.value.length && fGaap.value && rootOfGaap(fGaap.value) !== rootOfCat(fCategory.value)){
      formErr.value = 'The GAAP category must sit under the same group as the category.'; return;
    }
    saving.value = true; formErr.value = '';
    const body = {
      code: fCode.value.trim(), name: fName.value.trim(), category_id: Number(fCategory.value),
      control_type: fControl.value || null, is_bank: fBank.value, is_active: fActive.value,
      description: fDesc.value.trim() || null,
      sort_order: fSort.value === '' ? null : Number(fSort.value),
      default_tax_code_id: fTax.value ? Number(fTax.value) : null,
    };
    // only where the country has a GAAP list: elsewhere the code has none to keep
    if(gaapCats.value.length) body.gaap_category_id = fGaap.value ? Number(fGaap.value) : null;
    try {
      const editing = !!formId.value;
      const res = editing ? await api.put('/admin/accounts/' + formId.value, body)
                          : await api.post('/admin/countries/' + id + '/accounts', body);
      formOpen.value = false;
      if(editing){ await loadAccounts(true); flashRow(formId.value); }
      else { await revealRow((lid) => loadAccounts(true, lid), res && res.id); }
    } catch(err2) {
      formErr.value = (err2.data && err2.data.error) || err2.message || 'Save failed';
    }
    saving.value = false;
  }

  // ---- country edit ----
  function openEditCountry(){
    const c = country.value || {};
    eIso2.value = c.iso2 || ''; eIso3.value = c.iso3 || ''; eName.value = c.name || '';
    eLang.value = c.default_language_id || ''; eCurr.value = c.default_currency_id || '';
    eDate.value = c.date_format || 'yyyy-MM-dd'; eTaxLabel.value = c.tax_label || 'VAT';
    eStd.value = c.accounting_standard || 'IFRS';
    cErr.value = ''; cOpen.value = true; focusModal();
  }
  function closeCountry(){ cOpen.value = false; }
  async function saveCountry(e){
    e.preventDefault();
    if(!eIso2.value.trim() || !eName.value.trim() || !eLang.value || !eCurr.value || !eStd.value){
      cErr.value = 'ISO2, name, language, currency and accounting standard are required.'; return;
    }
    cSaving.value = true; cErr.value = '';
    const body = { iso2: eIso2.value.trim(), iso3: eIso3.value.trim() || null, name: eName.value.trim(),
                   default_language_id: Number(eLang.value), default_currency_id: Number(eCurr.value),
                   date_format: eDate.value, tax_label: eTaxLabel.value.trim() || 'VAT',
                   accounting_standard: eStd.value };
    try {
      await api.put('/admin/countries/' + id, body);
      // Update the view optimistically from the form + locally-resolved labels — a re-fetch
      // here can race the write's commit (read-after-write) and show stale values.
      const lang = langs.value.find(l => String(l.id) === String(body.default_language_id));
      const cur = currs.value.find(c => String(c.id) === String(body.default_currency_id));
      const prev = country.value || {};
      country.value = { ...prev, id: Number(id),
        iso2: body.iso2, iso3: body.iso3, name: body.name, date_format: body.date_format,
        tax_label: body.tax_label, accounting_standard: body.accounting_standard,
        default_language_id: body.default_language_id, default_currency_id: body.default_currency_id,
        language_code: lang ? lang.code : prev.language_code, language_name: lang ? lang.name : prev.language_name,
        currency_code: cur ? cur.code : prev.currency_code, currency_symbol: cur ? cur.symbol : prev.currency_symbol,
        currency_name: cur ? cur.name : prev.currency_name };
      cOpen.value = false;
    } catch(err2){ cErr.value = (err2.data && err2.data.error) || err2.message || 'Save failed'; }
    cSaving.value = false;
  }

  // ---- pricing tier open/close/save/delete ----
  function openTierCreate(){
    tId.value = null; tCode.value = ''; tName.value = ''; tMonth.value = ''; tYear.value = '';
    tTxn.value = ''; tTurnover.value = ''; tUsers.value = ''; tSort.value = ''; tActive.value = true; tTerminal.value = false;
    tErr.value = ''; tOpen.value = true; focusModal();
  }
  function openTierEdit(t){
    tId.value = t.id; tCode.value = t.code || ''; tName.value = t.name || '';
    tMonth.value = t.price_month ?? ''; tYear.value = t.price_year ?? '';
    tTxn.value = t.max_txn_count ?? ''; tTurnover.value = t.max_turnover ?? ''; tUsers.value = t.max_users ?? '';
    tSort.value = t.sort_order ?? ''; tActive.value = t.is_active !== false; tTerminal.value = !!t.is_terminal;
    tErr.value = ''; tOpen.value = true; focusModal();
  }
  function closeTier(){ tOpen.value = false; }
  async function saveTier(e){
    e.preventDefault();
    if(!tCode.value.trim() || !tName.value.trim()){ tErr.value = 'Code and name are required.'; return; }
    tSaving.value = true; tErr.value = '';
    const body = {
      code: tCode.value.trim(), name: tName.value.trim(),
      price_month: tMonth.value === '' ? 0 : tMonth.value,
      price_year: tYear.value === '' ? null : tYear.value,
      max_txn_count: tTxn.value === '' ? null : tTxn.value,
      max_turnover: tTurnover.value === '' ? null : tTurnover.value,
      max_users: tUsers.value === '' ? null : tUsers.value,
      sort_order: tSort.value === '' ? 0 : tSort.value,
      is_active: tActive.value, is_terminal: tTerminal.value,
    };
    try {
      if(tId.value) await api.put('/admin/tiers/' + tId.value, body);
      else await api.post('/admin/tiers', { ...body, country_id: Number(id) });   // per-country (country_id in body)
      tOpen.value = false; await loadTiers(true);
    } catch(err2){ tErr.value = (err2.data && err2.data.error) || err2.message || 'Save failed'; }
    tSaving.value = false;
  }

  // ---- entity type open/close/save/delete ----
  function openEtCreate(){
    etId.value = null; etCode.value = ''; etName.value = ''; etSort.value = ''; etActive.value = true;
    etErr.value = ''; etOpen.value = true; focusModal();
  }
  function openEtEdit(t){
    etId.value = t.id; etCode.value = t.code || ''; etName.value = t.name || '';
    etSort.value = t.sort_order ?? ''; etActive.value = t.is_active !== false;
    etErr.value = ''; etOpen.value = true; focusModal();
  }
  function closeEt(){ etOpen.value = false; }
  async function saveEt(e){
    e.preventDefault();
    if(!etCode.value.trim() || !etName.value.trim()){ etErr.value = 'Code and name are required.'; return; }
    etSaving.value = true; etErr.value = '';
    const body = { code: etCode.value.trim(), name: etName.value.trim(),
                   sort_order: etSort.value === '' ? 0 : Number(etSort.value), is_active: etActive.value };
    try {
      if(etId.value) await api.put('/admin/entity-types/' + etId.value, body);
      else await api.post('/admin/countries/' + id + '/entity-types', body);
      etOpen.value = false; await loadEntityTypes(true);
    } catch(err2){ etErr.value = (err2.data && err2.data.error) || err2.message || 'Save failed'; }
    etSaving.value = false;
  }

  // ---- default-codes picker (which country accounts seed a company of this type) ----
  async function openCodes(t){
    codesEtId.value = t.id; codesEtName.value = t.name; codesErr.value = ''; codeQ.value = '';
    codesOpen.value = true; codesLoading.value = true; codeSel.value = []; focusModal();
    try {
      if(!allAccts.value.length){
        // API caps per_page at 200, so page through to load the WHOLE chart.
        let page = 1, all = [];
        for(;;){
          const r = await api.get('/admin/countries/' + id + '/accounts?per_page=200&page=' + page);
          all = all.concat(r.data || []);
          if(all.length >= (r.total || 0) || !(r.data || []).length) break;
          page++;
        }
        allAccts.value = all;
      }
      const cur = await api.get('/admin/entity-types/' + t.id + '/accounts');
      codeSel.value = cur.account_ids || [];
      // prefill each selected account's editable name: the per-type override if set, else the base
      // account name (so the admin edits the effective name they'll get on a new company).
      const descs = cur.descriptions || {};
      for(const aid of codeSel.value) dsig(aid).value = descs[String(aid)] || acctName(aid);
    } catch { codeSel.value = []; }
    codesLoading.value = false;
  }
  function closeCodes(){ codesOpen.value = false; }
  function toggleCode(aid){
    const s = new Set(codeSel.value);
    if(s.has(aid)){ s.delete(aid); }
    else { s.add(aid); if(!(dsig(aid).value || '').trim()) dsig(aid).value = acctName(aid); }  // seed with the base name
    codeSel.value = [...s];
  }
  async function saveCodes(){
    codesSaving.value = true; codesErr.value = '';
    // send an override only where the admin changed the name; a value equal to the base name (or
    // blank) clears the override server-side so the master account name is used.
    const descriptions = {};
    for(const aid of codeSel.value){
      const v = (dsig(aid).value || '').trim();
      descriptions[String(aid)] = (v && v !== acctName(aid)) ? v : '';
    }
    try { await api.put('/admin/entity-types/' + codesEtId.value + '/accounts',
                        { account_ids: codeSel.value, descriptions });
          codesOpen.value = false; await loadEntityTypes(true); }
    catch(err2){ codesErr.value = (err2.data && err2.data.error) || err2.message || 'Save failed'; }
    codesSaving.value = false;
  }

  // ---- tab bodies ----
  function field(label, val){
    return html`<div class="field"><div class="field-label">${label}</div><div class="field-val">${val}</div></div>`;
  }
  const dateFmtLabel = v => {
    const f = DATE_FORMATS.find(d => d.v === v);
    return f ? f.v + ' (' + f.ex + ')' : (v || 'yyyy-MM-dd');
  };
  const localeView = () => {
    const c = country.value;
    return html`
      <div class="tab-actions">
        <span class="count-line">Defaults for ${c.name}</span>
        <button class="btn btn-primary btn-sm" @click=${openEditCountry}>✎ Edit country</button>
      </div>
      <div class="detail-grid">
        ${field('Default language', c.language_name ? c.language_name + ' (' + c.language_code + ')' : '—')}
        ${field('Default currency', c.currency_code ? c.currency_symbol + ' ' + c.currency_code + ' — ' + c.currency_name : '—')}
        ${field('Date format', dateFmtLabel(c.date_format))}
        ${field('Tax regime', c.tax_regime_name ? (c.tax_regime_name + (c.tax_regime_code ? ' (' + c.tax_regime_code + ')' : '')) : 'Not set')}
        ${field('Accounting standard', c.accounting_standard || 'IFRS')}
        ${field('ISO codes', c.iso2 + ' · ' + c.iso3)}
      </div>
      <div class="hint">Languages are managed in the <b>Languages</b> section — here you set this country's default. Companies inherit the date format and may override it. A company created here takes the country's accounting standard; existing companies keep theirs.</div>`;
  };

  // NOTE: ledgerView is returned by `body`, which lives in a reactive ${body} slot. A NESTED
  // `${() => ...}` reactive block inside a view that is itself swapped into a slot APPENDS on
  // every re-render in this tina4js version (the inner block's anchors break). So we read all
  // signals at the top (acctPage/acctPer/accounts) — that makes `body` re-run on any of them —
  // and use plain `${value}` interpolation only. `body` then replaces the whole view cleanly.
  const ledgerView = () => {
    const a = accounts.value;
    const page = acctPage.value, per = acctPer.value;   // read so `body` tracks these signals
    const dId = delId.value, dBusy = deleting.value, dErr = delErr.value;   // track delete state
    const hl = flashId.value;                                               // row to highlight
    const taxList = taxCodes.value.data;                                    // read so the badge re-renders when codes load
    const taxMap = {}; (taxList || []).forEach(tc => { taxMap[tc.id] = tc; });
    const taxBadge = tid => (tid && taxMap[tid])
      ? html`<span class="chart-vat" title=${'Default ' + vatLabel() + ' code'}>${taxMap[tid].code}</span>` : '';
    const gaapMap = {}; gaapCats.value.forEach(g => { gaapMap[g.id] = g; });   // read so the chips draw when the list loads
    const gaapBadge = gid => (gid && gaapMap[gid])
      ? html`<span class="chart-gaap" title=${'GAAP category: ' + gaapMap[gid].name}>${gaapMap[gid].code}</span>` : '';
    const listBody = !a.loaded
      ? html`<div class="muted-block">Loading…</div>`
      : !a.data.length
      ? html`<div class="muted-block">No accounts yet — add the first one.</div>`
      : html`
        ${dErr ? html`<div class="alert alert-danger py-2 mb-2">${dErr}</div>` : null}
        <div class="cat-cards">${a.data.map(x => html`
          <div class=${'cat-card' + (x.is_active === false ? ' inactive' : '') + (hl === x.id ? ' row-hl' : '')}>
            <span class="cat-code">${x.code}</span>
            <span class="cat-name">${x.name}${x.is_active === false ? html`<span class="tag-off">inactive</span>` : ''}</span>
            ${gaapBadge(x.gaap_category_id)}
            ${taxBadge(x.default_tax_code_id)}
            <button class="btn btn-sm btn-outline cat-edit" title="Edit" @click=${() => openEdit(x)}>✎</button>
            ${delCtl('acct:' + x.id, '/admin/accounts/' + x.id, () => loadAccounts(true))}
          </div>`)}</div>`;
    return html`
      <div class="tab-actions">
        <span class="count-line">${a.loaded ? a.total + ' accounts' : 'Loading…'}</span>
        <div class="tab-actions-right">
          ${perPageSelect(per, onAcctPer)}
          <button class="btn btn-primary btn-sm" @click=${openCreate}>+ Add account</button>
        </div>
      </div>
      ${pager({ page, totalPages: a.total_pages, total: a.loaded ? a.total : 0,
                perPage: Number(per), onPage: acctGoPage })}
      ${listBody}
      ${pager({ page, totalPages: a.total_pages, total: a.loaded ? a.total : 0,
                perPage: Number(per), onPage: acctGoPage })}`;
  };

  // Slotted view → read signals at top, plain `${value}` interpolation only (no nested
  // `${() => …}` — see CLAUDE.md). Prices show in the country's currency symbol.
  const pricingView = () => {
    const t = tiers.value;
    const c = country.value || {};
    const sym = c.currency_symbol || '';
    const cur = c.currency_code || '';
    // each tier is priced in ITS OWN currency (tier.currency_id) — the country's default currency can
    // have changed since (owner 2026-10-19: no currency bleeds into another)
    const cl = currs.value;
    const tierSym = tier => {
      if(tier.currency_id === null || tier.currency_id === undefined || tier.currency_id === '') return sym;
      const tc = cl.find(x => String(x.id) === String(tier.currency_id));
      return tc ? (tc.symbol || tc.code || '') : '';
    };
    const dId = delId.value, dBusy = deleting.value, dErr = delErr.value;   // track delete state
    const head = html`
      <div class="tab-actions">
        <span class="count-line">${t.loaded
          ? t.total + ' tier' + (t.total === 1 ? '' : 's') + ' · ' + (c.name || 'this country') + (cur ? ' · ' + cur : '')
          : 'Loading…'}</span>
        <button class="btn btn-primary btn-sm" @click=${openTierCreate}>+ Add tier</button>
      </div>`;
    if(!t.loaded) return html`${head}<div class="muted-block">Loading…</div>`;
    if(!t.data.length) return html`${head}<div class="muted-block">No pricing tiers for ${c.name || 'this country'} yet.<br>
      <span class="muted-sub">Add tiers priced in ${cur || 'this country\'s currency'} — by transaction count + turnover (monthly / yearly).</span></div>`;
    return html`${head}
      ${dErr ? html`<div class="alert alert-danger py-2 mb-2">${dErr}</div>` : null}
      <div class="tier-grid">${t.data.map(tier => html`
        <div class=${'tier card' + (tier.is_active === false ? ' inactive' : '')}>
          <div class="card-body">
            <div class="tier-head">
              <span class="tier-name">${tier.name}</span>
              <span class="tier-head-actions">
                <button class="btn btn-sm btn-outline cat-edit" title="Edit" @click=${() => openTierEdit(tier)}>✎</button>
                ${delCtl('tier:' + tier.id, '/admin/tiers/' + tier.id, () => loadTiers(true))}
              </span>
            </div>
            <div class="tier-code">${tier.code}</div>
            <div class="tier-price">${tierSym(tier)}${tier.price_month ?? '0'}<span class="tier-per"> /mo</span></div>
            ${tier.price_year != null && tier.price_year !== ''
              ? html`<div class="tier-year">${tierSym(tier)}${tier.price_year} /yr</div>` : ''}
            <div class="tier-caps">≤ ${tier.max_txn_count ?? '∞'} txns · ≤ ${tier.max_turnover != null ? tierSym(tier) + tier.max_turnover : '∞'} turnover · ≤ ${tier.max_users ?? '∞'} users</div>
            <div class="tier-badges">
              ${tier.is_terminal ? html`<span class="badge badge-info">Top tier</span>` : ''}
              ${tier.is_active === false ? html`<span class="tag-off">inactive</span>` : ''}
            </div>
          </div>
        </div>`)}</div>`;
  };

  // Slotted view → read signals at top, plain `${value}` interpolation only (no nested `${() => …}`).
  const entityView = () => {
    const et = entityTypes.value;
    const dId = delId.value, dBusy = deleting.value, dErr = delErr.value;   // track delete state
    const head = html`
      <div class="tab-actions">
        <span class="count-line">${et.loaded ? et.total + ' entity type' + (et.total === 1 ? '' : 's') : 'Loading…'}</span>
        <button class="btn btn-primary btn-sm" @click=${openEtCreate}>+ Add entity type</button>
      </div>`;
    if(!et.loaded) return html`${head}<div class="muted-block">Loading…</div>`;
    if(!et.data.length) return html`${head}<div class="muted-block">No entity types yet.<br>
      <span class="muted-sub">Add the legal entity types in this country (e.g. Pty Ltd, Trust) — each gets default ledger codes that seed a new company's chart.</span></div>`;
    return html`${head}
      ${dErr ? html`<div class="alert alert-danger py-2 mb-2">${dErr}</div>` : null}
      <div class="cat-cards">${et.data.map(t => html`
        <div class=${'cat-card' + (t.is_active === false ? ' inactive' : '')}>
          <span class="cat-code">${t.code}</span>
          <span class="cat-name">${t.name}${t.is_active === false ? html`<span class="tag-off">inactive</span>` : ''}</span>
          <button class="btn btn-sm btn-outline" @click=${() => openCodes(t)}>Default codes</button>
          <button class="btn btn-sm btn-outline cat-edit" title="Edit" @click=${() => openEtEdit(t)}>✎</button>
          ${delCtl('et:' + t.id, '/admin/entity-types/' + t.id, () => loadEntityTypes(true))}
        </div>`)}</div>`;
  };

  // Slotted view → read signals at top, plain `${value}` interpolation only (no nested `${() => …}`).
  const emailView = () => {
    const e = emailTpls.value;
    const c = country.value || {};
    const head = html`
      <div class="tab-actions">
        <span class="count-line">${e.loaded ? e.data.length + ' email template' + (e.data.length === 1 ? '' : 's') + ' · ' + (c.name || 'this country') : 'Loading…'}</span>
      </div>`;
    if(!e.loaded) return html`${head}<div class="muted-block">Loading…</div>`;
    return html`${head}
      <div class="hint">The emails aboo sends. Each country can customise them — an unedited one uses the built-in default. Use <code>{{variables}}</code> for the dynamic bits.</div>
      <div class="cat-cards">${e.data.map(t => html`
        <div class="cat-card tpl-card" @click=${() => openTemplate(t)}>
          <span class="row-main"><b>${t.label}</b><span class="row-sub">${t.description}</span></span>
          ${t.is_custom
            ? html`<span class="badge badge-info">Custom</span>`
            : html`<span class="badge badge-light">Default</span>`}
          <button class="btn btn-sm btn-outline cat-edit" title="Edit" @click=${() => openTemplate(t)}>✎</button>
        </div>`)}</div>`;
  };

  // Slotted view → read signals at top, plain `${value}` interpolation only (no nested `${() => …}`).
  const taxView = () => {
    const tx = taxCodes.value;
    const c = country.value || {};
    const dId = delId.value, dBusy = deleting.value, dErr = delErr.value;   // track delete state
    const head = html`
      <div class="tab-actions">
        <span class="count-line">${tx.loaded
          ? tx.data.length + ' ' + vatLabel() + ' code' + (tx.data.length === 1 ? '' : 's') + ' · ' + (c.name || 'this country')
          : 'Loading…'}</span>
        <div class="tab-actions-right">
          <button class="btn btn-outline btn-sm" @click=${openRegime}>⚙ Reporting fields</button>
          <button class="btn btn-primary btn-sm" @click=${openTaxCreate}>+ Add ${vatLabel()} code</button>
        </div>
      </div>`;
    if(!tx.loaded) return html`${head}<div class="muted-block">Loading…</div>`;
    if(!tx.data.length) return html`${head}<div class="muted-block">No ${vatLabel()} codes for ${c.name || 'this country'} yet.<br>
      <span class="muted-sub">Add the ${vatLabel()} codes used here (e.g. Standard 15%, Zero-rated, Exempt).</span></div>`;
    const fields = (tx.regime && tx.regime.statutory_fields) || [];
    return html`${head}
      ${dErr ? html`<div class="alert alert-danger py-2 mb-2">${dErr}</div>` : null}
      <div class="cat-cards">${tx.data.map(t => {
        const typ = TAX_KIND_LABEL(t.kind).replace(/\s*\(.*\)$/, '') + (t.is_capital ? ' – Capital' : '');
        const stat = fields.map(f => (t.statutory && t.statutory[f.key]) ? f.label + ' ' + t.statutory[f.key] : null)
                           .filter(Boolean).join(' · ');
        return html`
        <div class=${'cat-card' + (t.is_active === false ? ' inactive' : '')}>
          <span class="cat-code">${t.code}</span>
          <span class="row-main"><b>${t.name}</b><span class="row-sub">${typ}${stat ? ' · ' + stat : ''}</span></span>
          <span class="badge badge-secondary">${Number(t.rate)}%</span>
          <button class="btn btn-sm btn-outline cat-edit" title="Edit" @click=${() => openTaxEdit(t)}>✎</button>
          ${delCtl('tax:' + t.id, '/admin/tax-codes/' + t.id, () => loadTaxCodes(true))}
        </div>`;
      })}</div>`;
  };

  const body = () => {
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    if(!country.value) return html`<div class="muted-block">Loading…</div>`;
    const t = activeTab.value;
    return t === 'locale' ? localeView() : t === 'ledger' ? ledgerView()
         : t === 'entities' ? entityView() : t === 'tax' ? taxView()
         : t === 'email' ? emailView() : pricingView();
  };

  const content = html`
    <div class="page">
      <div class="page-head">
        <a class="back-link" @click=${() => navigate('/countries')}>${icon('chevron-left', {size: 14})} Countries</a>
        <h1 class="page-title">${() => country.value ? country.value.name : 'Country'}</h1>
        <div class="page-sub">${() => country.value ? (country.value.iso2 + ' · ' + country.value.iso3) : ''}</div>
      </div>
      <div class="tabbar">
        ${() => TABS.map(tab => html`
          <button class=${'tab' + (activeTab.value === tab.key ? ' active' : '')} @click=${() => setTab(tab.key)}>${tab.key === 'tax' ? vatLabel() + ' codes' : tab.label}</button>`)}
      </div>
      <div class="tab-body">
        <input class="form-control ledger-search" type="search" placeholder="Search code or name…"
               ?hidden=${() => activeTab.value !== 'ledger'} @input=${onAcctSearch}>
        ${body}
      </div>

      <!-- account form (always in DOM; toggled — keeps input focus) -->
      <div class="modal-overlay" ?hidden=${() => !formOpen.value}>
        <form class="modal-card" @submit=${save}>
          <div class="modal-head">
            <h3>${() => formId.value ? 'Edit account' : 'Add account'}</h3>
            <button type="button" class="modal-x" @click=${closeForm}>✕</button>
          </div>
          <label class="form-label">Code</label>
          <input class="form-control mb-2" maxlength="4" .value=${fCode}
                 @input=${e => { const v = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase(); e.target.value = v; fCode.value = v; }}
                 placeholder="e.g. 8400 — 4 letters/digits">
          <div class="form-note">Exactly 4 letters or digits — no dots, spaces or symbols (flat codes, no sub-accounts).</div>
          <label class="form-label">Name</label>
          <input class="form-control mb-2" .value=${fName} @input=${e => { fName.value = e.target.value; }} placeholder="e.g. Bank Charges">
          <label class="form-label">Category</label>
          <select class="form-control mb-2" .value=${fCategory} @change=${onCategory}>
            <option value="">— Select a category —</option>
            ${() => cats.value.map(c => html`<option value=${c.id}>${c.code} — ${c.name}</option>`)}
          </select>
          <div class="form-note">Normal side (debit/credit) is set automatically from the category.</div>
          <!-- only where the country has its own GAAP list: the code's line in it, under the same group -->
          <div ?hidden=${() => !gaapCats.value.length}>
            <label class="form-label">GAAP category</label>
            <select class="form-control mb-2" .value=${fGaap} @change=${e => { fGaap.value = e.target.value; }}>
              <option value="">— Select a GAAP category —</option>
              ${() => catRoots.value.filter(r => gaapCats.value.some(g => g.parent_id === r.id)).map(r => html`<optgroup label=${r.name} ?disabled=${() => !!fCategory.value && rootOfCat(fCategory.value) !== r.id}>
                ${gaapCats.value.filter(g => g.parent_id === r.id).map(g => html`<option value=${g.id}>${g.code} — ${g.name}</option>`)}
              </optgroup>`)}
            </select>
            ${() => { const group = gaapGroupMissing();
                      return group ? html`<div class="alert alert-warning py-2 mb-2 gaap-missing">This country's GAAP list has no category under ${group} yet. Add one first (Ledger categories › GAAP).</div>` : null; }}
            <div class="form-note">Where the code sits in this country's GAAP list — under the same group as its category. A company created while the country uses GAAP takes it.</div>
          </div>
          <label class="form-label">Control type</label>
          <select class="form-control mb-2" .value=${fControl} @change=${e => { fControl.value = e.target.value; }}>
            <option value="">— not a control account —</option>
            ${CONTROL_TYPES.map(t => html`<option value=${t}>${t}</option>`)}
          </select>
          <label class="form-label">${() => 'Default ' + vatLabel() + ' code'}</label>
          <select class="form-control mb-2" @change=${e => { fTax.value = e.target.value; }}>
            <option value="" ?selected=${() => !fTax.value}>${() => '— No ' + vatLabel() + ' —'}</option>
            ${() => taxCodes.value.data.map(tc => html`<option value=${tc.id} ?selected=${() => String(tc.id) === String(fTax.value)}>${tc.code}${tc.name ? ' · ' + tc.name : ''}${tc.rate != null && tc.rate !== '' ? ' (' + tc.rate + '%)' : ''}</option>`)}
          </select>
          <label class="form-check"><input type="checkbox" .checked=${fBank} @change=${e => { fBank.value = e.target.checked; }}> Bank / cashbook account</label>
          <label class="form-check"><input type="checkbox" .checked=${fActive} @change=${e => { fActive.value = e.target.checked; }}> Active</label>
          <label class="form-label">Description</label>
          <textarea class="form-control mb-2" rows="2" .value=${fDesc} @input=${e => { fDesc.value = e.target.value; }} placeholder="Optional — notes for capturers"></textarea>
          <label class="form-label">Sort order</label>
          <input class="form-control mb-2" type="number" .value=${fSort} @input=${e => { fSort.value = e.target.value; }} placeholder="e.g. 100 — display order">
          ${() => formErr.value ? html`<div class="alert alert-danger py-2 mb-2">${formErr}</div>` : null}
          <div class="form-actions">
            <button type="button" class="btn btn-outline" @click=${closeForm}>Cancel</button>
            <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>

      <!-- country edit form -->
      <div class="modal-overlay" ?hidden=${() => !cOpen.value}>
        <form class="modal-card" @submit=${saveCountry}>
          <div class="modal-head"><h3>Edit country</h3><button type="button" class="modal-x" @click=${closeCountry}>✕</button></div>
          <label class="form-label">ISO-2 code</label>
          <input class="form-control mb-2" maxlength="2" .value=${eIso2} @input=${e => { eIso2.value = e.target.value; }} placeholder="e.g. PA">
          <label class="form-label">ISO-3 code</label>
          <input class="form-control mb-2" maxlength="3" .value=${eIso3} @input=${e => { eIso3.value = e.target.value; }} placeholder="e.g. PAN (optional)">
          <label class="form-label">Name</label>
          <input class="form-control mb-2" .value=${eName} @input=${e => { eName.value = e.target.value; }} placeholder="e.g. Panama">
          <label class="form-label">Default language</label>
          <select class="form-control mb-2" .value=${eLang} @change=${e => { eLang.value = e.target.value; }}>
            <option value="">— Select a language —</option>
            ${() => langs.value.map(l => html`<option value=${l.id}>${l.name} (${l.code})</option>`)}
          </select>
          <label class="form-label">Default currency</label>
          <select class="form-control mb-2" .value=${eCurr} @change=${e => { eCurr.value = e.target.value; }}>
            <option value="">— Select a currency —</option>
            ${() => currs.value.map(c => html`<option value=${c.id}>${c.symbol} ${c.code} — ${c.name}</option>`)}
          </select>
          <label class="form-label">Default date format</label>
          <select class="form-control mb-2" .value=${eDate} @change=${e => { eDate.value = e.target.value; }}>
            ${DATE_FORMATS.map(d => html`<option value=${d.v}>${d.v} — ${d.ex}</option>`)}
          </select>
          <label class="form-label">Tax label</label>
          <input class="form-control mb-2" maxlength="20" .value=${eTaxLabel} @input=${e => { eTaxLabel.value = e.target.value; }} placeholder="e.g. VAT, GST, Sales tax">
          <div class="form-note">What this country calls its tax — names the tax-codes tab (e.g. “VAT codes”).</div>
          <label class="form-label">Accounting standard</label>
          <select class="form-control mb-2" .value=${eStd} @change=${e => { eStd.value = e.target.value; }}>
            <option value="">— Select a standard —</option>
            <option value="IFRS">IFRS</option>
            <option value="GAAP">GAAP — the country's own list</option>
          </select>
          <div class="form-note">A company created in this country takes it; existing companies keep theirs. GAAP needs the country's GAAP list (Ledger categories › GAAP) and a GAAP category on every ledger code.</div>
          ${() => cErr.value ? html`<div class="alert alert-danger py-2 mb-2">${cErr}</div>` : null}
          <div class="form-actions">
            <button type="button" class="btn btn-outline" @click=${closeCountry}>Cancel</button>
            <button type="submit" class="btn btn-primary" ?disabled=${() => cSaving.value}>${() => cSaving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>

      <!-- pricing tier form -->
      <div class="modal-overlay" ?hidden=${() => !tOpen.value}>
        <form class="modal-card" @submit=${saveTier}>
          <div class="modal-head">
            <h3>${() => tId.value ? 'Edit tier' : 'Add tier'}</h3>
            <button type="button" class="modal-x" @click=${closeTier}>✕</button>
          </div>
          <label class="form-label">Code</label>
          <input class="form-control mb-2" .value=${tCode} @input=${e => { tCode.value = e.target.value; }} placeholder="e.g. STARTER">
          <label class="form-label">Name</label>
          <input class="form-control mb-2" .value=${tName} @input=${e => { tName.value = e.target.value; }} placeholder="e.g. Starter">
          <div class="form-row">
            <div class="form-col">
              <label class="form-label">Monthly price</label>
              <input class="form-control mb-2" type="number" step="0.01" min="0" .value=${tMonth} @input=${e => { tMonth.value = e.target.value; }} placeholder="e.g. 199.00">
            </div>
            <div class="form-col">
              <label class="form-label">Yearly price</label>
              <input class="form-control mb-2" type="number" step="0.01" min="0" .value=${tYear} @input=${e => { tYear.value = e.target.value; }} placeholder="e.g. 1990.00 (optional)">
            </div>
          </div>
          <div class="form-row">
            <div class="form-col">
              <label class="form-label">Max transactions</label>
              <input class="form-control mb-2" type="number" min="0" .value=${tTxn} @input=${e => { tTxn.value = e.target.value; }} placeholder="blank = unlimited">
            </div>
            <div class="form-col">
              <label class="form-label">Max turnover</label>
              <input class="form-control mb-2" type="number" step="0.01" min="0" .value=${tTurnover} @input=${e => { tTurnover.value = e.target.value; }} placeholder="blank = unlimited">
            </div>
          </div>
          <label class="form-label">Max users</label>
          <input class="form-control mb-2" type="number" min="1" .value=${tUsers} @input=${e => { tUsers.value = e.target.value; }} placeholder="max users per company — blank = unlimited">
          <div class="form-note">Caps how many users can be invited to a company on this tier.</div>
          <label class="form-label">Sort order</label>
          <input class="form-control mb-2" type="number" .value=${tSort} @input=${e => { tSort.value = e.target.value; }} placeholder="e.g. 10 — display order">
          <label class="form-check"><input type="checkbox" .checked=${tActive} @change=${e => { tActive.value = e.target.checked; }}> Active</label>
          <label class="form-check"><input type="checkbox" .checked=${tTerminal} @change=${e => { tTerminal.value = e.target.checked; }}> Top tier (no upgrade beyond this)</label>
          <div class="form-note">${() => country.value && country.value.currency_code ? 'Priced in ' + country.value.currency_code + '.' : ''}</div>
          ${() => tErr.value ? html`<div class="alert alert-danger py-2 mb-2">${tErr}</div>` : null}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeTier}>Cancel</button>
            <button type="submit" class="btn btn-primary" ?disabled=${() => tSaving.value}>${() => tSaving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>

      <!-- entity type form -->
      <div class="modal-overlay" ?hidden=${() => !etOpen.value}>
        <form class="modal-card" @submit=${saveEt}>
          <div class="modal-head">
            <h3>${() => etId.value ? 'Edit entity type' : 'Add entity type'}</h3>
            <button type="button" class="modal-x" @click=${closeEt}>✕</button>
          </div>
          <label class="form-label">Code</label>
          <input class="form-control mb-2" .value=${etCode} @input=${e => { etCode.value = e.target.value; }} placeholder="e.g. PTY">
          <label class="form-label">Name</label>
          <input class="form-control mb-2" .value=${etName} @input=${e => { etName.value = e.target.value; }} placeholder="e.g. Private Company (Pty) Ltd">
          <label class="form-label">Sort order</label>
          <input class="form-control mb-2" type="number" .value=${etSort} @input=${e => { etSort.value = e.target.value; }} placeholder="e.g. 10 — display order">
          <label class="form-check"><input type="checkbox" .checked=${etActive} @change=${e => { etActive.value = e.target.checked; }}> Active</label>
          ${() => etErr.value ? html`<div class="alert alert-danger py-2 mb-2">${etErr}</div>` : null}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeEt}>Cancel</button>
            <button type="submit" class="btn btn-primary" ?disabled=${() => etSaving.value}>${() => etSaving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>

      <!-- default-codes picker -->
      <div class="modal-overlay" ?hidden=${() => !codesOpen.value}>
        <div class="modal-card modal-wide">
          <div class="modal-head">
            <h3>Default codes — ${() => codesEtName.value}</h3>
            <button type="button" class="modal-x" @click=${closeCodes}>✕</button>
          </div>
          <div class="form-note">${() => codeSel.value.length} of ${() => allAccts.value.length} accounts selected — these are created for a new company of this type. Tick a code to edit the name it gets for this entity type.</div>
          <input class="form-control mb-2" type="search" placeholder="Search code or name…" .value=${codeQ} @input=${e => { codeQ.value = e.target.value; }}>
          <div class="code-picker">
            ${() => {
              if(codesLoading.value) return html`<div class="muted-block">Loading…</div>`;
              const q = codeQ.value.trim().toLowerCase();
              const sel = new Set(codeSel.value);
              const list = allAccts.value.filter(a => !q || (a.code + ' ' + a.name).toLowerCase().includes(q));
              if(!list.length) return html`<div class="muted-block">No accounts match.</div>`;
              // A ticked row shows an EDITABLE name input (bound to the account's own signal → typing
              // never re-runs this slot, so it keeps focus); an unticked row shows the static name.
              return list.map(a => { const on = sel.has(a.id); return html`
                <div class=${'code-row' + (on ? ' on' : '')}>
                  <input type="checkbox" ?checked=${on} @change=${() => toggleCode(a.id)}
                         aria-label=${'Include ' + a.code + ' ' + a.name}>
                  <span class="cat-code">${a.code}</span>
                  ${on
                    ? html`<input class="form-control code-desc" .value=${dsig(a.id)}
                             @input=${e => { dsig(a.id).value = e.target.value; }}
                             placeholder=${a.name} aria-label=${'Name for ' + a.code}>`
                    : html`<span class="code-name">${a.name}</span>`}
                </div>`; });
            }}
          </div>
          ${() => codesErr.value ? html`<div class="alert alert-danger py-2 mb-2">${codesErr}</div>` : null}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeCodes}>Cancel</button>
            <button type="button" class="btn btn-primary" @click=${saveCodes} ?disabled=${() => codesSaving.value}>${() => codesSaving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>

      <!-- email template editor -->
      <div class="modal-overlay" ?hidden=${() => !teOpen.value}>
        <div class="modal-card modal-wide">
          <div class="modal-head">
            <h3>Edit email — ${() => teLabel.value} · ${() => country.value ? country.value.name : ''}</h3>
            <button type="button" class="modal-x" @click=${closeTemplate}>✕</button>
          </div>
          <label class="form-label">Subject</label>
          <input class="form-control mb-2" .value=${teSubject} @input=${e => { teSubject.value = e.target.value; }} placeholder="e.g. Reset your aboo password">
          <label class="form-label">Body (HTML)</label>
          <div class="tpl-vars">
            <span class="tpl-vars-lbl">Insert:</span>
            ${() => teVars.value.map(v => html`<button type="button" class="tpl-var" @click=${() => insertVar(v)}>{{${v}}}</button>`)}
          </div>
          <textarea id="te-body" class="form-control tpl-body mb-2" rows="9" spellcheck="false"
                    .value=${teBody} @input=${e => { teBody.value = e.target.value; }}
                    placeholder="HTML — wrapped in the aboo brand shell when sent"></textarea>
          <div class="tpl-preview-head">
            <label class="form-check"><input type="checkbox" .checked=${tePreview} @change=${e => { tePreview.value = e.target.checked; }}> Live preview</label>
            <span class="form-note">Preview uses sample values.</span>
          </div>
          <div class="tpl-preview-wrap" ?hidden=${() => !tePreview.value}>
            <iframe class="tpl-preview" sandbox="allow-same-origin" title="preview" .srcdoc=${() => previewHtml()}></iframe>
          </div>
          ${() => teErr.value ? html`<div class="alert alert-danger py-2 mb-2">${teErr}</div>` : null}
          <div class="form-actions">
            ${() => teCustom.value
              ? html`<button type="button" class="btn btn-outline btn-danger-text" @click=${resetTemplate} ?disabled=${() => teSaving.value}>Reset to default</button>`
              : html`<button type="button" class="btn btn-outline" @click=${useDefault}>Load default</button>`}
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeTemplate}>Cancel</button>
            <button type="button" class="btn btn-primary" @click=${saveTemplate} ?disabled=${() => teSaving.value}>${() => teSaving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>

      <!-- tax code form -->
      <div class="modal-overlay" ?hidden=${() => !txOpen.value}>
        <form class="modal-card" @submit=${saveTax}>
          <div class="modal-head">
            <h3>${() => (txId.value ? 'Edit ' : 'Add ') + vatLabel() + ' code'}</h3>
            <button type="button" class="modal-x" @click=${closeTax}>✕</button>
          </div>
          <label class="form-label">Code</label>
          <input class="form-control mb-2" .value=${txCode} @input=${e => { txCode.value = e.target.value; }} placeholder="e.g. 01, STD, ZR">
          <label class="form-label">Name</label>
          <input class="form-control mb-2" .value=${txName} @input=${e => { txName.value = e.target.value; }} placeholder="e.g. Standard rate">
          <label class="form-label">Rate (%)</label>
          <input class="form-control mb-2" type="number" step="0.0001" min="0" max="100" .value=${txRate} @input=${e => { txRate.value = e.target.value; }} placeholder="e.g. 15">
          <label class="form-label">Type</label>
          <select class="form-control mb-2" .value=${txKind} @change=${e => { txKind.value = e.target.value; }}>
            ${TAX_KINDS.map(k => html`<option value=${k.v}>${k.l}</option>`)}
          </select>
          <label class="form-check mb-2"><input type="checkbox" .checked=${txCapital} @change=${e => { txCapital.value = e.target.checked; }}> Capital goods (e.g. Output – Capital)</label>
          ${() => { txOpen.value; const fs = statFields();   // slot keyed on open; inputs bind per-field signals → focus-safe
            return fs.length ? html`<div class="tpl-vars-lbl mb-1">Reporting codes</div>${fs.map(f => html`
              <label class="form-label">${f.label}</label>
              <input class="form-control mb-2" .value=${statSig(f.key)} @input=${e => { statSig(f.key).value = e.target.value; }} placeholder=${'e.g. ' + f.label}>`)}` : ''; }}
          <label class="form-label">Sort order</label>
          <input class="form-control mb-2" type="number" .value=${txSort} @input=${e => { txSort.value = e.target.value; }} placeholder="e.g. 10 — display order">
          <label class="form-check"><input type="checkbox" .checked=${txActive} @change=${e => { txActive.value = e.target.checked; }}> Active</label>
          ${() => txErr.value ? html`<div class="alert alert-danger py-2 mb-2">${txErr}</div>` : null}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeTax}>Cancel</button>
            <button type="submit" class="btn btn-primary" ?disabled=${() => txSaving.value}>${() => txSaving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>

      <!-- regime reporting-fields editor -->
      <div class="modal-overlay" ?hidden=${() => !rgOpen.value}>
        <div class="modal-card">
          <div class="modal-head">
            <h3>${() => vatLabel()} reporting fields</h3>
            <button type="button" class="modal-x" @click=${closeRegime}>✕</button>
          </div>
          <div class="form-note">Statutory codes each ${() => vatLabel()} code carries — e.g. SARS code, VAT SARS code (VAT201 box). Other countries define their own.</div>
          <label class="form-label">Regime name</label>
          <input class="form-control mb-2" .value=${rgName} @input=${e => { rgName.value = e.target.value; }} placeholder="e.g. South Africa VAT">
          <label class="form-label">Reporting fields</label>
          ${() => { rgNonce.value; return rgFields.value.length ? rgFields.value.map((f, i) => html`
            <div class="rg-field-row">
              <input class="form-control" .value=${f.label} @input=${e => { f.label.value = e.target.value; }} placeholder="e.g. SARS code">
              <button type="button" class="btn btn-sm btn-outline" title="Remove" @click=${() => removeField(i)}>✕</button>
            </div>`) : html`<div class="muted-sub mb-2">No reporting fields — codes will just have a rate + type.</div>`; }}
          <button type="button" class="btn btn-sm btn-outline mb-2" @click=${addField}>+ Add field</button>
          ${() => rgErr.value ? html`<div class="alert alert-danger py-2 mb-2">${rgErr}</div>` : null}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeRegime}>Cancel</button>
            <button type="button" class="btn btn-primary" @click=${saveRegime} ?disabled=${() => rgSaving.value}>${() => rgSaving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>`;

  return shell('countries', 'Countries', content);
}
